<div class="deviation-report-container">
  <!-- Header -->
  <div class="page-header">
    <div class="header-content">
      <h2 class="page-title">
        <mat-icon>compare_arrows</mat-icon>
        Labour Deviation Report
      </h2>
      <div class="header-actions">
        <button class="btn-secondary" (click)="generateReport()" [disabled]="loading">
          <mat-icon>refresh</mat-icon>
          <span>Refresh</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Filters Card -->
  <div class="filter-card">
    <div class="filter-grid">
      <div class="filter-item">
        <mat-icon>business</mat-icon>
        <select class="filter-select" [(ngModel)]="selectedProject" (change)="onFilterChange()">
          <option value="">All Projects</option>
          <option *ngFor="let project of projects" [value]="project.id">
            {{ project.project_name }}
          </option>
        </select>
      </div>
      
      <div class="filter-item">
        <mat-icon>calendar_today</mat-icon>
        <input
          type="date"
          class="filter-input"
          [(ngModel)]="startDate"
          (change)="onFilterChange()"
          placeholder="Start Date"
        />
      </div>
      
      <div class="filter-item">
        <mat-icon>event</mat-icon>
        <input
          type="date"
          class="filter-input"
          [(ngModel)]="endDate"
          (change)="onFilterChange()"
          placeholder="End Date"
        />
      </div>

      <div class="filter-item">
        <mat-icon>view_list</mat-icon>
        <select class="filter-select" [(ngModel)]="groupBy" (change)="onGroupByChange()">
          <option value="date">Group by Date</option>
          <option value="project">Group by Project</option>
        </select>
      </div>

      <button class="btn-filter" (click)="generateReport()" [disabled]="loading">
        <mat-icon>filter_list</mat-icon>
        <span>Generate Report</span>
      </button>
    </div>
  </div>

  <!-- Summary Card -->
  <div *ngIf="reportData?.summary" class="summary-card">
    <h3 class="summary-title">Summary</h3>
    <div class="summary-grid">
      <div class="summary-item">
        <mat-icon>description</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Total Records</span>
          <span class="summary-value">{{ reportData.summary.total_records }}</span>
        </div>
      </div>
      <div class="summary-item">
        <mat-icon>business</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Total Projects</span>
          <span class="summary-value">{{ reportData.summary.total_projects }}</span>
        </div>
      </div>
      <div class="summary-item" [ngClass]="getDeviationClass(reportData.summary.total_carpenters_deviation)">
        <mat-icon>{{ getDeviationIcon(reportData.summary.total_carpenters_deviation) }}</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Carpenters Deviation</span>
          <span class="summary-value">{{ reportData.summary.total_carpenters_deviation }}</span>
        </div>
      </div>
      <div class="summary-item" [ngClass]="getDeviationClass(reportData.summary.total_fitters_deviation)">
        <mat-icon>{{ getDeviationIcon(reportData.summary.total_fitters_deviation) }}</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Fitters Deviation</span>
          <span class="summary-value">{{ reportData.summary.total_fitters_deviation }}</span>
        </div>
      </div>
      <div class="summary-item" [ngClass]="getDeviationClass(reportData.summary.total_masons_deviation)">
        <mat-icon>{{ getDeviationIcon(reportData.summary.total_masons_deviation) }}</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Masons Deviation</span>
          <span class="summary-value">{{ reportData.summary.total_masons_deviation }}</span>
        </div>
      </div>
      <div class="summary-item" [ngClass]="getDeviationClass(reportData.summary.total_deviation)">
        <mat-icon>{{ getDeviationIcon(reportData.summary.total_deviation) }}</mat-icon>
        <div class="summary-info">
          <span class="summary-label">Total Deviation</span>
          <span class="summary-value">{{ reportData.summary.total_deviation }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Report Table -->
  <div class="table-card">
    <div class="table-responsive-modern">
      <table class="deviation-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Date</th>
            <th>Project</th>
            <th colspan="4" class="category-header">Carpenters</th>
            <th colspan="4" class="category-header">Fitters</th>
            <th colspan="4" class="category-header">Masons</th>
            <th colspan="4" class="category-header">Total</th>
          </tr>
          <tr class="sub-header">
            <th></th>
            <th></th>
            <th></th>
            <th>Planned</th>
            <th>Actual</th>
            <th>Deviation</th>
            <th>%</th>
            <th>Planned</th>
            <th>Actual</th>
            <th>Deviation</th>
            <th>%</th>
            <th>Planned</th>
            <th>Actual</th>
            <th>Deviation</th>
            <th>%</th>
            <th>Planned</th>
            <th>Actual</th>
            <th>Deviation</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let report of reports; let i = index" class="deviation-row">
            <td class="cell-center">{{ i + 1 }}</td>
            <td>
              <span class="date-badge">{{ formatDate(report.plan_date) }}</span>
            </td>
            <td class="project-cell">
              <strong>{{ report.project_name || report.project_identifier }}</strong>
            </td>
            
            <!-- Carpenters -->
            <td class="cell-right">{{ report.carpenters.planned }}</td>
            <td class="cell-right">{{ report.carpenters.actual }}</td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.carpenters.deviation)">
              <span [ngClass]="'deviation-badge ' + getDeviationClass(report.carpenters.deviation)">
                {{ report.carpenters.deviation > 0 ? '+' : '' }}{{ report.carpenters.deviation }}
              </span>
            </td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.carpenters.deviation)">
              {{ report.carpenters.deviation_percent.toFixed(1) }}%
            </td>
            
            <!-- Fitters -->
            <td class="cell-right">{{ report.fitters.planned }}</td>
            <td class="cell-right">{{ report.fitters.actual }}</td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.fitters.deviation)">
              <span [ngClass]="'deviation-badge ' + getDeviationClass(report.fitters.deviation)">
                {{ report.fitters.deviation > 0 ? '+' : '' }}{{ report.fitters.deviation }}
              </span>
            </td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.fitters.deviation)">
              {{ report.fitters.deviation_percent.toFixed(1) }}%
            </td>
            
            <!-- Masons -->
            <td class="cell-right">{{ report.masons.planned }}</td>
            <td class="cell-right">{{ report.masons.actual }}</td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.masons.deviation)">
              <span [ngClass]="'deviation-badge ' + getDeviationClass(report.masons.deviation)">
                {{ report.masons.deviation > 0 ? '+' : '' }}{{ report.masons.deviation }}
              </span>
            </td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.masons.deviation)">
              {{ report.masons.deviation_percent.toFixed(1) }}%
            </td>
            
            <!-- Total -->
            <td class="cell-right"><strong>{{ report.total.planned }}</strong></td>
            <td class="cell-right"><strong>{{ report.total.actual }}</strong></td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.total.deviation)">
              <span [ngClass]="'deviation-badge ' + getDeviationClass(report.total.deviation)">
                {{ report.total.deviation > 0 ? '+' : '' }}{{ report.total.deviation }}
              </span>
            </td>
            <td class="cell-right" [ngClass]="getDeviationClass(report.total.deviation)">
              <strong>{{ report.total.deviation_percent.toFixed(1) }}%</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div *ngIf="loading" class="loading-overlay">
      <mat-spinner diameter="50"></mat-spinner>
      <p>Generating report...</p>
    </div>

    <div *ngIf="!loading && (!reports || reports.length === 0)" class="empty-state">
      <mat-icon>assessment</mat-icon>
      <h3>No Deviation Data Found</h3>
      <p>No matching records found for the selected criteria</p>
    </div>
  </div>
</div>

